import { createInstance, OptimizelyProvider } from "@optimizely/react-sdk";
import { useEffect, useState } from "react";
import { useGeoLoc } from "@/libs/geo/GeoLocProvider";
import { UNAUTHENTICATED_OPTIMIZELY_USER } from "./constants";
import { getOptimizelyLogLevel } from "./helpers";
import { isNil } from "lodash";

type Props = React.PropsWithChildren<{
  dataFromOptimizely?: string | object;
  upmId?: null | string;
}>;

const OptimizelyProviderWrapper: React.FC<Props> = ({
  dataFromOptimizely,
  children,

  // if you are using a pages route (the old way) you need
  // to pass in the upmId via serverside props to be able to control whether
  // the a specific user can access a page on user level
  upmId,
}) => {
  const { country } = useGeoLoc();
  /***
   *  When initializing with just the datafile, the SDK will use the given datafile.
   *  When initializing with just the SDK key, the datafile will be automatically downloaded.
   *  When initializing with both the SDK key and datafile, the SDK will use the given datafile to start,
   *  then download the latest version of the datafile in the background.
   *
   *  We just want to use the datafile here because the singleton is updating it already
   *  every OPTIMIZELY_REFRESH_INTERVAL so the FE does not have to keep fetching the datafile over
   *  and over.
   */
  const [optimizelyClient] = useState(() => {
    return createInstance({
      datafile: dataFromOptimizely,
      logLevel: getOptimizelyLogLevel(),
      datafileOptions: {
        autoUpdate: false,
      },
      odpOptions: {
        disabled: true,
      },
    });
  });
  /***
   *  On the browser side, optimizely.close() is automatically connected to the pagehide event,
   *  so there is no need to do any manual instrumentation. But we are leaving this here
   *  for now just in case
   */
  useEffect(() => {
    return () => {
      optimizelyClient.close();
    };
  }, [optimizelyClient]);

  return (
    <OptimizelyProvider
      timeout={3000}
      optimizely={optimizelyClient}
      user={getOptimizelyUserInfo(country, upmId)}
    >
      {children}
    </OptimizelyProvider>
  );
};

const getOptimizelyUserInfo = (country?: string, upmId?: string | null) => {
  const userInfoObject = {
    id: isNil(upmId) ? UNAUTHENTICATED_OPTIMIZELY_USER : upmId,
    attributes: {
      upmId: isNil(upmId) ? UNAUTHENTICATED_OPTIMIZELY_USER : upmId,
      geoloc_cookie: country ?? "",
    },
  };

  return userInfoObject;
};

export default OptimizelyProviderWrapper;
